import { useTranslation } from "react-i18next";
import { MicOff, VideoOff } from "lucide-react";
import type { MediaErrorCause } from "../lib/deviceErrors";

interface DeviceAffordanceProps {
  kind: "camera" | "microphone";
  cause: MediaErrorCause;
  className?: string;
}

/**
 * Persistent, actionable message shown when the local camera or mic can't be
 * acquired (GH-1179). Rendered inline (in the video-tile placeholder for the
 * camera, next to the mic control for the mic) rather than as an ephemeral
 * toast, so it stays visible and screenshottable while the device is blocked.
 *
 * Copy is cause-specific: `blocked` → enable in browser settings; `no-device`
 * → nothing detected; `unknown` → generic couldn't-access fallback.
 */
export function DeviceAffordance({ kind, cause, className }: DeviceAffordanceProps) {
  const { t } = useTranslation();
  const Icon = kind === "camera" ? VideoOff : MicOff;

  return (
    <div
      data-testid={`${kind}-affordance`}
      data-cause={cause}
      role="status"
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium leading-snug ${className ?? ""}`}
      style={{ background: "rgba(0,0,0,0.65)", color: "#fff" }}
    >
      <Icon className="size-3.5 shrink-0" strokeWidth={2.5} />
      <span>{t(`call.deviceAffordance.${kind}.${cause}`)}</span>
    </div>
  );
}
